<template>
  <el-dialog
    :model-value="modelValue"
    :title="reminder ? $t('views.notifications.reminder.editTitle') : $t('views.notifications.reminder.addTitle')"
    width="480px"
    :close-on-click-modal="false"
    @update:model-value="$emit('update:modelValue', $event)"
    @closed="onClosed"
  >
    <div class="flex flex-col gap-4">
      <!-- Title -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ $t('views.notifications.reminder.titleLabel') }} <span class="text-red-500">*</span>
        </label>
        <el-input v-model="form.title" :placeholder="$t('views.notifications.reminder.titleLabel')" />
        <p v-if="errors.title" class="text-xs text-red-500 mt-1">{{ errors.title }}</p>
      </div>

      <!-- Note -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('views.notifications.reminder.noteLabel') }}</label>
        <el-input v-model="form.note" type="textarea" :rows="3" :placeholder="$t('views.notifications.reminder.noteLabel')" />
      </div>

      <!-- Date & Time -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ $t('views.notifications.reminder.dateTimeLabel') }} <span class="text-red-500">*</span>
        </label>
        <el-date-picker
          v-model="form.remindAt"
          type="datetime"
          :placeholder="$t('views.notifications.reminder.dateTimeLabel')"
          class="w-full"
          format="YYYY-MM-DD hh:mm A"
          value-format="YYYY-MM-DDTHH:mm:ssZ"
        />
        <p v-if="errors.remindAt" class="text-xs text-red-500 mt-1">{{ errors.remindAt }}</p>
      </div>

      <!-- Repeat -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('views.notifications.reminder.repeatLabel') }}</label>
        <el-select v-model="form.repeatType" class="w-full">
          <el-option value=""        :label="$t('views.notifications.repeat.none')" />
          <el-option value="hourly"  :label="$t('views.notifications.repeat.hourly')" />
          <el-option value="daily"   :label="$t('views.notifications.repeat.daily')" />
          <el-option value="weekly"  :label="$t('views.notifications.repeat.weekly')" />
          <el-option value="monthly" :label="$t('views.notifications.repeat.monthly')" />
        </el-select>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between">
        <!-- Left: delete + done when editing -->
        <div class="flex gap-2">
          <el-button
            v-if="reminder"
            type="danger"
            plain
            :loading="deleting"
            @click="onDelete"
          >
            {{ $t('views.notifications.reminder.delete') }}
          </el-button>
          <el-button
            v-if="reminder"
            type="success"
            plain
            :loading="markingDone"
            @click="onMarkDone"
          >
            {{ $t('views.notifications.reminder.markDone') }}
          </el-button>
        </div>

        <!-- Right: cancel + save/ok -->
        <div class="flex gap-2">
          <el-button @click="$emit('update:modelValue', false)">
            {{ $t('dialogs.cancel') }}
          </el-button>
          <el-button type="primary" :loading="saving" @click="onSave">
            {{ reminder ? $t('update') : 'OK' }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import Notification from '@/models/notification/Notification'

export default defineComponent({
  name: 'ReminderDialog',

  props: {
    modelValue: { type: Boolean, required: true },
    reminder:   { type: Object as PropType<Notification | null>, default: null },
  },

  emits: ['update:modelValue', 'saved', 'deleted'],

  setup(props, { emit }) {
    const { t } = useI18n()
    const http  = useHTTP()

    const saving     = ref(false)
    const deleting   = ref(false)
    const markingDone = ref(false)

    const form = ref({
      title:      '',
      note:       '',
      remindAt:   '' as string,
      repeatType: '',
    })

    const errors = ref({ title: '', remindAt: '' })

    const populateForm = () => {
      if (props.reminder) {
        form.value.title      = props.reminder.title
        form.value.note       = props.reminder.subTitle
        form.value.remindAt   = props.reminder.date
        form.value.repeatType = props.reminder.repeatType
      } else {
        form.value.title      = ''
        form.value.note       = ''
        form.value.remindAt   = ''
        form.value.repeatType = ''
      }
      errors.value = { title: '', remindAt: '' }
    }

    watch(() => props.modelValue, (val) => {
      if (val) populateForm()
    })

    const validate = (): boolean => {
      errors.value = { title: '', remindAt: '' }
      let ok = true
      if (!form.value.title.trim()) {
        errors.value.title = t('views.notifications.reminder.titleLabel') + ' is required'
        ok = false
      }
      if (!form.value.remindAt) {
        errors.value.remindAt = t('views.notifications.reminder.dateTimeLabel') + ' is required'
        ok = false
      }
      return ok
    }

    const onSave = async () => {
      if (!validate()) return
      saving.value = true
      try {
        const payload = {
          title:      form.value.title.trim(),
          note:       form.value.note.trim(),
          remindAt:   form.value.remindAt,
          repeatType: form.value.repeatType,
        }
        if (props.reminder) {
          await http.put(`${Notification.ENDPOINT_REMINDERS}/${props.reminder.entityID}`, payload)
        } else {
          await http.post(Notification.ENDPOINT_REMINDERS, payload)
        }
        emit('saved')
        emit('update:modelValue', false)
      } catch {
        // error handled by http plugin
      } finally {
        saving.value = false
      }
    }

    const onDelete = async () => {
      if (!props.reminder) return
      deleting.value = true
      try {
        await http.delete(`${Notification.ENDPOINT_REMINDERS}/${props.reminder.entityID}`)
        emit('deleted')
        emit('update:modelValue', false)
      } catch {
        // error handled by http plugin
      } finally {
        deleting.value = false
      }
    }

    const onMarkDone = async () => {
      if (!props.reminder) return
      markingDone.value = true
      try {
        await http.post(`${Notification.ENDPOINT_REMINDERS}/${props.reminder.entityID}/done`, {})
        emit('deleted')
        emit('update:modelValue', false)
      } catch {
        // error handled by http plugin
      } finally {
        markingDone.value = false
      }
    }

    const onClosed = () => {
      populateForm()
    }

    return {
      form,
      errors,
      saving,
      deleting,
      markingDone,
      onSave,
      onDelete,
      onMarkDone,
      onClosed,
    }
  },
})
</script>
